import React from 'react';
import type { Summary } from '|/app/services';
import CardBrandIcon, { isKnownCardBrand } from '|/components/carvana-ds/card-brand-icon';
import ApplePayMark from '|/icons/apple-pay-mark.svg';
import GooglePayMark from '|/icons/google-pay-mark.svg';
import MicrOnUs from '|/icons/micr-on-us.svg';
import MicrTransit from '|/icons/micr-transit.svg';
import styles from './checkout-summary.module.css';

interface PaymentCheckoutSummaryProps {
  summary: Summary | null;
}

const getDigitalWalletDisplay = (digitalWallet?: string | null) => {
  switch (digitalWallet) {
    case 'ApplePay':
      return {
        icon: <ApplePayMark className={styles.walletMark} aria-hidden="true" />,
        label: 'Apple Pay'
      };
    case 'GooglePay':
      return {
        icon: <GooglePayMark className={styles.walletMark} aria-hidden="true" />,
        label: 'Google Pay'
      };
    default:
      return {
        icon: null,
        label: 'Apple Pay or Google Pay'
      };
  }
};

const paymentSelectionLabels = {
  CashiersCheck: "Cashier's check",
  WireTransfer: 'Wire transfer'
} as const;

type PaymentAccount = NonNullable<NonNullable<Summary['payment']>['paymentAccounts']>[number];

const getPaymentSelectionDisplay = (account: PaymentAccount) => {
  switch (account.selectionType) {
    case 'BankAccount':
      if (!account.mask) return 'Account ****';

      return (
        <>
          Account
          <MicrTransit width={9} height={12} aria-hidden="true" />
          &middot;&middot;&middot;&middot;{account.mask}
          <MicrOnUs width={9} height={9} aria-hidden="true" />
        </>
      );
    case 'Card':
      if (!account.mask) return 'Card ****';

      return (
        <>
          <CardBrandIcon brand={account.cardBrand ?? undefined} />
          {!isKnownCardBrand(account.cardBrand ?? undefined) && 'Card '}
          &middot;&middot;&middot;{account.mask}
        </>
      );
    case 'DigitalWallet':
      return getDigitalWalletDisplay(account.digitalWallet);
    case 'CashiersCheck':
    case 'WireTransfer':
      return paymentSelectionLabels[account.selectionType];
    default:
      return null;
  }
};

const PaymentCheckoutSummary: React.FC<PaymentCheckoutSummaryProps> = ({ summary }) => {
  const account = summary?.payment?.paymentAccounts?.find(a => a.type === 'DownPayment');

  if (!account) return null;

  const display = getPaymentSelectionDisplay(account);

  if (!display) return null;

  if (typeof display === 'object' && 'label' in display) {
    return (
      <span className={styles.row} aria-label={display.label}>
        {display.icon}
        {!display.icon && display.label}
      </span>
    );
  }

  return <span className={styles.row}>{display}</span>;
};

export default PaymentCheckoutSummary;
